// src\themes\tolica\elements\Header\HeaderNavAction.tsx
'use client';

import Link from 'next/link';
import { User, LogOut, LayoutDashboard, ShieldCheck } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCartStore } from '@/core/hooks/cart/useCartStore';
import { useAuth } from '@/contexts/authContext';
import { useDictionary } from '@/core/hooks/use-dictionary';
import { HeaderNavSearch } from './HeaderNavSearch';
import HeaderSelecetLanguage from './HeaderSelecetLanguage';
import { usePathname } from 'next/navigation';

export default function HeaderNavAction() {
  const { cart } = useCartStore();
  const { dictionary } = useDictionary();
  const { state, clearAuth, hasPermission } = useAuth();
  const pathname = usePathname();

  const totalItems = cart?.items?.reduce(
    (sum, item) => sum + (item.quantity || 0),
    0
  );

  const isLoggedIn = state?.isAuthenticated;
  const user = state?.user;

  // Create login URL with return parameter
  const loginHref = `/login?returnUrl=${encodeURIComponent(pathname)}`;

  return (
    <div className="flex justify-end items-center gap-2 sm:gap-4">

      {/* 🔍 Search */}
      <HeaderNavSearch />

      {/* 🌍 Language Selector */}
      <div className="max-lg:hidden flex items-center h-full">
        <HeaderSelecetLanguage variant="mini" />
      </div>


      {/* 👤 User Section */}
      <div className="hidden lg:flex items-center">
        {isLoggedIn ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                aria-label="User menu"
                className="flex items-center gap-2"
              >
                <div
                  className="flex items-center gap-2 text-sm text-primary-0">
                  <User className="size-5" strokeWidth={1} />
                </div>
                <span className="text-sm font-medium text-primary-0 hidden xl:block">
                  {user?.full_name || 'کاربر عزیز'}
                </span>
              </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
              align="start"
              sideOffset={8}
              className="w-52 p-0 rounded-none shadow-xl border border-primary-200 overflow-hidden"
            >
              {/* Header */}
              <DropdownMenuLabel className="px-4 py-3 border-b border-primary-100 bg-primary-50/60 rounded-none">
                <p className="text-xs text-primary-600 mb-0.5">خوش آمدید</p>
                <p className="text-sm text-primary-900">
                  {user?.full_name || 'کاربر عزیز'}
                </p>
              </DropdownMenuLabel>

              {/* Links */}
              <div className="py-1">
                <DropdownMenuItem asChild>
                  <Link
                    href="/profile"
                    className="flex items-center justify-between px-4 py-2.5 cursor-pointer text-sm text-primary-900 rounded-none focus:bg-primary-50 focus:text-primary-900"
                  >
                    <span>{dictionary.forms.profile}</span>
                    <User className="size-3.5 text-primary-500" strokeWidth={1} />
                  </Link>
                </DropdownMenuItem>

                <DropdownMenuItem asChild>
                  <Link
                    href="/dashboard"
                    className="flex items-center justify-between px-4 py-2.5 cursor-pointer text-sm text-primary-900 rounded-none focus:bg-primary-50 focus:text-primary-900"
                  >
                    <span>{dictionary.dashboard.title}</span>
                    <LayoutDashboard className="size-3.5 text-primary-500" strokeWidth={1} />
                  </Link>
                </DropdownMenuItem>

                {hasPermission('AccessToAdmin') && (
                  <DropdownMenuItem asChild>
                    <Link
                      href="/admin"
                      className="flex items-center justify-between px-4 py-2.5 cursor-pointer text-sm text-primary-900 rounded-none focus:bg-primary-50 focus:text-primary-900"
                    >
                      <span>{dictionary.ui.profile.adminPanel}</span>
                      <ShieldCheck className="size-3.5 text-primary-500" strokeWidth={1} />
                    </Link>
                  </DropdownMenuItem>
                )}
              </div>

              <DropdownMenuSeparator className="my-0 bg-primary-100" />

              {/* Logout */}
              <div className="py-1">
                <DropdownMenuItem
                  onClick={clearAuth}
                  className="flex items-center justify-between px-4 py-2.5 cursor-pointer text-sm text-red-500 rounded-none focus:bg-red-50 focus:text-red-600"
                >
                  <span>{dictionary.common.logout}</span>
                  <LogOut className="size-3.5 text-red-600" strokeWidth={1} />
                </DropdownMenuItem>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Link
            href={loginHref}
            aria-label="User login"
            className="flex items-center gap-2 text-sm text-primary-0"
          >
            <User className="size-5 text-primary-0" strokeWidth={1} />
            <span className="hidden xl:block">ورود / ثبت‌نام</span>
          </Link>
        )}
      </div>

      {/* 🛒 Cart */}
      <Link
        href="/cart"
        aria-label="Shopping cart"
        className="relative p-2 text-primary-0 group flex items-center justify-center"
      >
        <svg className="size-5" viewBox="0 0 24 24" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path strokeMiterlimit="10" d="M7.1802 7.58C7.1802 4.36 9.2402 1.75 11.7902 1.75C14.3402 1.75 16.4002 4.36 16.4002 7.58M11.7902 7.58C2.0402 7.58 0.690197 6.97 2.3502 16.18C3.3002 21.48 3.9802 22.22 11.7902 22.22C19.6002 22.22 20.2802 21.47 21.2302 16.18C22.8902 6.97 21.5502 7.58 11.7902 7.58Z"></path>
        </svg>

        {totalItems > 0 && (
          <span className="absolute top-0.5 font-primary-bold right-0 flex items-center justify-center min-w-3.5 h-3.5 bg-red-500 text-white text-[10px]">
            {totalItems > 99 ? '99+' : totalItems}
          </span>
        )}
      </Link>

    </div >
  );
}